// Wire types for every admin overview endpoint, plus the small normalised shape
// the shared filter toolbar works on. Provider rows stay in their own shapes:
// Daytona carries live metrics, GCP carries volumes and port slugs, Lambda
// carries image versions, and flattening those into one row would lose all of it.

export type Viewer = {
  id: string;
  email?: string;
  roles?: string;
};

// ─── /api/admin/overview ─────────────────────────────────────────────────────

export type AdminUser = {
  userId: string;
  sandboxStatus: "running" | "persisted" | "none";
  sandboxId: string | null;
  hasActiveAgent: boolean;
  lastUsed: number | null;
  createdAt: number | null;
  source: "memory" | "persisted" | "none";
};

export type AdminSprite = {
  sandboxId: string;
  shortSandboxId: string;
  status: "running" | "persisted" | "orphaned" | "unknown";
  ownerUserId: string | null;
  ownerSource: "memory" | "persisted" | "unknown";
  managed: boolean;
  createdAt: number | null;
  lastUsed: number | null;
};

export type AdminOverview = {
  generatedAt: string;
  users: AdminUser[];
  sprites: AdminSprite[];
  spritesEnabled: boolean;
};

// ─── /api/admin/lambda-microvms/overview ─────────────────────────────────────

export type AdminLambdaMicrovm = {
  microvmId: string;
  state: string;
  imageArn: string;
  imageVersion: string;
  startedAt: string | null;
};

export type AdminLambdaOverview = {
  provider: "lambda_microvms";
  region: string;
  imageArn: string;
  generatedAt: string;
  microvms: AdminLambdaMicrovm[];
};

// ─── /api/admin/daytona/overview ─────────────────────────────────────────────

export type AdminDaytonaSandboxMetrics = {
  cpuCount: number | null;
  cpuUsedPct: number | null;
  diskTotal: number | null;
  diskUsed: number | null;
  memTotal: number | null;
  memUsed: number | null;
  timestamp: string | null;
};

export type AdminDaytonaSandbox = {
  sandboxId: string;
  name: string;
  state: string;
  snapshot: string | null;
  target: string | null;
  cpu: number | null;
  memory: number | null;
  disk: number | null;
  createdAt: string | null;
  lastActivityAt: string | null;
  autoStopInterval: number | null;
  autoDeleteInterval: number | null;
  errorReason: string | null;
  metrics: AdminDaytonaSandboxMetrics | null;
};

export type AdminDaytonaRegionUsage = {
  regionId: string;
  sandboxClass: string;
  totalCpuQuota: number | null;
  currentCpuUsage: number | null;
  totalMemoryQuota: number | null;
  currentMemoryUsage: number | null;
  totalDiskQuota: number | null;
  currentDiskUsage: number | null;
  totalGpuQuota: number | null;
  currentGpuUsage: number | null;
};

export type AdminDaytonaUsage = {
  regionUsage: AdminDaytonaRegionUsage[];
  totalSnapshotQuota: number | null;
  currentSnapshotUsage: number | null;
  totalVolumeQuota: number | null;
  currentVolumeUsage: number | null;
};

export type AdminDaytonaOverview = {
  provider: "daytona";
  target: string;
  snapshot: string;
  generatedAt: string;
  sandboxes: AdminDaytonaSandbox[];
  usage: AdminDaytonaUsage | null;
  usageMessage?: string;
};

// ─── /api/admin/gcp/overview ─────────────────────────────────────────────────

export type AdminGcpInstance = {
  resourceId: string;
  instanceId: string;
  state: string;
  ownerUserId: string | null;
  ownerEmail: string | null;
  projectIds: string[];
  projectNames: string[];
  shared: boolean;
  connectedUsers: number;
  zone: string;
  machineType?: string;
  instanceTemplate?: string;
  homeDiskName?: string;
  portSlugs?: string[];
  volume: {
    name: string;
    status: "attached" | "detached" | "missing" | "unknown";
    sizeGb?: number;
    detail?: string;
  };
  core: {
    status: "healthy" | "unhealthy" | "unknown";
    detail?: string;
  };
};

export type AdminGcpUser = {
  userId: string;
  activeWorkspaceId: string | null;
  activeProvider: "sprites" | "gcp_compute" | null;
  hasActiveAgent: boolean;
};

export type AdminGcpOverview = {
  generatedAt: string;
  configured: boolean;
  config: {
    projectId: string;
    zone: string;
    instanceTemplate: string;
    homeDiskDeviceName: string;
    homeDiskType: string;
    homeDiskSizeGb: number;
  } | null;
  users: AdminGcpUser[];
  instances: AdminGcpInstance[];
  errors: string[];
};

// ─── Shared vocabulary ───────────────────────────────────────────────────────

/**
 * One state vocabulary across every provider, so the filter toolbar and the
 * summary strip mean the same thing everywhere. Each section maps its own raw
 * state into this and still shows the provider's own word as the label.
 */
export type WorkspaceState = "running" | "stopped" | "orphaned" | "terminal" | "unknown";

export type HealthState = "healthy" | "unhealthy" | "unknown";

/**
 * Health as a filterable value. Only GCP reports Core health today, so every
 * other provider passes "n/a": those rows sit out the health filter entirely
 * rather than being hidden by it. "unknown" is different, and load-bearing: it
 * means a provider that does report health could not determine it.
 */
export type FilterableHealth = HealthState | "n/a";

export const PROVIDER_TABS = ["all", "users", "sprites", "daytona", "lambda", "gcp"] as const;
export type ProviderTab = (typeof PROVIDER_TABS)[number];

/**
 * The projection the shared filter runs against. A section builds one of these
 * per row, keeps its own rich row alongside, and renders only what survives.
 */
export type FilterableRow = {
  state: WorkspaceState;
  health: FilterableHealth;
  /** Everything a free-text search should match, already lowercased by `toFilterable`. */
  haystack: string;
};

/**
 * Tone mapping for the DS `Status` component. Kept here so every section agrees.
 *
 * The DS polices the accent by count, never two yellows in a viewport, so
 * warning is spent only on the one state an operator should actually act on:
 * an orphaned workspace has no owner and is still costing money. A stopped or
 * suspended workspace is resting, not alarming, so it takes the calm info
 * register. Terminal is danger because the workspace, and for GCP its disk,
 * is gone.
 */
export function stateTone(state: WorkspaceState): "success" | "warning" | "danger" | "info" {
  if (state === "running") return "success";
  if (state === "orphaned") return "warning";
  if (state === "terminal") return "danger";
  return "info";
}

export function healthTone(health: HealthState): "success" | "danger" | "info" {
  if (health === "healthy") return "success";
  if (health === "unhealthy") return "danger";
  return "info";
}
